import { FunctionComponent, useEffect, useState } from 'react'
import { Table, Button, Spinner, Alert } from 'react-bootstrap'
import Icon from 'components/ui/icon'
import { Link } from 'react-router-dom'

import Page from 'components/ui/page'
import PageHeader from 'components/ui/pageHeader'
import StatusBadge, { BadgeTone } from 'components/ui/statusBadge'
import { HospitalAvailable } from 'features/hospitals/types'
import * as hospitalApi from './api.client'
import * as doctorApi from '../doctors/api.client'
import * as accessRequestApi from '../accessRequest/api.client'
import { AccessRequestStatus } from 'features/accessRequest/types'

const ACCESS_TONES: Record<string, BadgeTone> = {
  APPROVED: 'success',
  REQUESTED: 'warning',
  DENIED: 'danger',
  REVOKED: 'danger',
  NOT_REQUESTED: 'neutral',
}

const Hospitals: FunctionComponent = () => {
  const [hospitals, setHospitals] = useState<HospitalAvailable[]>([])
  const [doctorAlreadyCreated, setDoctorAlreadyCreated] = useState(false)
  const [requestingHospitalId, setRequestingHospitalId] = useState<
    number | null
  >(null)
  const [errorMessage, setErrorMessage] = useState({ show: false, message: '' })

  useEffect(() => {
    checkIfDoctorRegisterIsCompleted()
    loadAvailableHospitals()
  }, [])

  const checkIfDoctorRegisterIsCompleted = async () => {
    try {
      const { doctorCreated } = await doctorApi.getDoctorFromLoggedUser()

      setDoctorAlreadyCreated(doctorCreated)
    } catch {}
  }

  const loadAvailableHospitals = async () => {
    try {
      const hospitals = await hospitalApi.getAvailableHospitals()
      setHospitals(hospitals)
    } catch (error) {
      setErrorMessage({
        show: true,
        message: 'Erro ao listar os hospitais disponíveis.',
      })
    }
  }

  const showRequestButton = (
    accessRequestStatus: AccessRequestStatus
  ): boolean => {
    if (!doctorAlreadyCreated) {
      return false
    }

    const statusThatShouldNotShowTheButton = [
      AccessRequestStatus.REQUESTED.toString(),
      AccessRequestStatus.APPROVED.toString(),
    ]

    return (
      statusThatShouldNotShowTheButton.findIndex(
        (s) => s === AccessRequestStatus[accessRequestStatus]
      ) === -1
    )
  }

  const requestAccess = async (hospitalId: number) => {
    try {
      setRequestingHospitalId(hospitalId)
      await accessRequestApi.requestAccess(hospitalId)
      loadAvailableHospitals()
    } catch (error) {
      setErrorMessage({
        show: true,
        message: 'Erro ao solicitar acesso ao hospital.',
      })
    } finally {
      setRequestingHospitalId(null)
    }
  }

  return (
    <Page fill>
      <PageHeader
        title="Hospitais"
        subtitle="Solicite acesso aos hospitais integrados para consultar seus repasses."
      />

      {errorMessage.show && <Alert variant="danger">{errorMessage.message}</Alert>}

      {!doctorAlreadyCreated && (
        <Alert variant="warning">
          Para solicitar acesso aos repasses, finalize o{' '}
          <Link to="/account" className="fw-medium">
            cadastro
          </Link>{' '}
          com seu CPF e telefone.
        </Alert>
      )}

      <div className="table-wrap">
        <Table hover>
          <thead>
            <tr>
              <th>Hospital</th>
              <th>Acesso</th>
              <th className="actions-col">
                <span className="visually-hidden">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {hospitals.map((hospital) => {
              const status = String(hospital.accessRequestStatus)
              const requesting = requestingHospitalId === hospital.id

              return (
                <tr key={hospital.id}>
                  <td className="fw-medium">{hospital.name}</td>
                  <td>
                    <StatusBadge tone={ACCESS_TONES[status] ?? 'neutral'}>
                      {AccessRequestStatus[hospital.accessRequestStatus]}
                    </StatusBadge>
                  </td>
                  <td className="actions-col">
                    {showRequestButton(hospital.accessRequestStatus) && (
                      <Button
                        variant="secondary"
                        size="sm"
                        disabled={requesting}
                        onClick={() => requestAccess(hospital.id)}
                      >
                        {requesting ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <Icon name="access" />
                        )}
                        {requesting ? 'Solicitando…' : 'Solicitar acesso'}
                      </Button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </Table>
      </div>
    </Page>
  )
}

export default Hospitals
